import React, { FunctionComponent, useState, useRef, useEffect } from "react";
import { useIntl, IntlShape, FormattedMessage } from "react-intl";
import classnames from "classnames";

import { KeyboardKeyInfo } from "../models/KeyboardKeyInfo";
import { useToggle } from "../../shared/utils/componentUtils";
import { KeyboardRow } from "./";

import "./Keyboard.scss";

interface KeyboardProps {
    inputElementId: string;
    onClose: () => void;
}

const inputElementActiveClassname = "has-open-keyboard";

export const Keyboard: FunctionComponent<KeyboardProps> = ({ inputElementId, onClose }) => {
    const intl = useIntl();
    const [isShift, toggleShift] = useToggle(false);
    const [keyData, setKeyData] = useState<KeyboardKeyInfo[][]>([]);
    // Transform style statement to relocate the keyboard container to the input element
    const [translateStatement, setTranslateStatement] = useState<string>("translate(0,0)");
    const inputElement = useRef<HTMLInputElement>(null);

    function init(): void {
        inputElement.current = document.getElementById(inputElementId) as HTMLInputElement;
        inputElement.current.classList.add(inputElementActiveClassname);

        const position = inputElement.current.getBoundingClientRect();
        setTranslateStatement(
            `translate(${position.left + window.scrollX}px, ${position.bottom + window.scrollY}px`
        );

        setKeyData(createKeyData(inputElement.current, intl));
    }

    function destroy(): void {
        inputElement.current.classList.remove(inputElementActiveClassname);
    }

    useEffect(() => {
        init();
        return destroy;
    }, []);

    return (
        <div
            className={classnames("keyboard_component", { "shift-enabled": isShift })}
            style={{ transform: translateStatement }}
        >
            {keyData.map((keyRow, idx) => (
                <KeyboardRow key={idx} data={keyRow} isShift={isShift} onShiftClick={toggleShift} />
            ))}
            <div className="close-button">
                <span onClick={onClose} data-testid="close-button">
                    <FormattedMessage id="common.button.close" />
                </span>
            </div>
        </div>
    );
};

function setNewInputValue(referenceElement: HTMLInputElement, newValue: string): void {
    referenceElement.value = newValue;
    const changeEvent = new Event("change");
    referenceElement.dispatchEvent(changeEvent);
}

function createKeyData(referenceElement: HTMLInputElement, intl: IntlShape): KeyboardKeyInfo[][] {
    function createRegularKeyInfo(keyValue: string, keyValueShift: string): KeyboardKeyInfo {
        return {
            display: keyValue,
            shiftDisplay: keyValueShift,
            type: "normal",
            onClick: (isShift: boolean) =>
                setNewInputValue(
                    referenceElement,
                    referenceElement.value + (isShift ? keyValueShift : keyValue)
                ),
        };
    }

    const backspaceKey: KeyboardKeyInfo = {
        display: "⌫",
        shiftDisplay: "⌫",
        type: "backspace",
        onClick: () => {
            if (referenceElement.value.length > 0) {
                setNewInputValue(
                    referenceElement,
                    referenceElement.value.substr(0, referenceElement.value.length - 1)
                );
            }
        },
    };

    const shiftKeyName = intl.formatMessage({ id: "keyboard.keyname.shift" });
    const shiftKey: KeyboardKeyInfo = {
        display: shiftKeyName,
        shiftDisplay: shiftKeyName,
        type: "shift",
        onClick: null, // Shift click is handled separately due to state
    };

    const spaceKeyName = intl.formatMessage({ id: "keyboard.keyname.space" });
    const spaceKey: KeyboardKeyInfo = {
        display: spaceKeyName,
        shiftDisplay: spaceKeyName,
        type: "space",
        onClick: () => setNewInputValue(referenceElement, referenceElement.value + " "),
    };

    return [
        [
            createRegularKeyInfo("`", "~"),
            createRegularKeyInfo("1", "!"),
            createRegularKeyInfo("2", "@"),
            createRegularKeyInfo("3", "#"),
            createRegularKeyInfo("4", "$"),
            createRegularKeyInfo("5", "%"),
            createRegularKeyInfo("6", "^"),
            createRegularKeyInfo("7", "&"),
            createRegularKeyInfo("8", "*"),
            createRegularKeyInfo("9", "("),
            createRegularKeyInfo("0", ")"),
            createRegularKeyInfo("-", "_"),
            createRegularKeyInfo("=", "+"),
            backspaceKey,
        ],
        [
            createRegularKeyInfo("q", "Q"),
            createRegularKeyInfo("w", "W"),
            createRegularKeyInfo("e", "E"),
            createRegularKeyInfo("r", "R"),
            createRegularKeyInfo("t", "T"),
            createRegularKeyInfo("y", "Y"),
            createRegularKeyInfo("u", "U"),
            createRegularKeyInfo("i", "I"),
            createRegularKeyInfo("o", "O"),
            createRegularKeyInfo("p", "P"),
            createRegularKeyInfo("[", "{"),
            createRegularKeyInfo("]", "}"),
            createRegularKeyInfo("\\", "|"),
        ],
        [
            createRegularKeyInfo("a", "A"),
            createRegularKeyInfo("s", "S"),
            createRegularKeyInfo("d", "D"),
            createRegularKeyInfo("f", "F"),
            createRegularKeyInfo("g", "G"),
            createRegularKeyInfo("h", "H"),
            createRegularKeyInfo("j", "J"),
            createRegularKeyInfo("k", "K"),
            createRegularKeyInfo("l", "L"),
            createRegularKeyInfo(";", ":"),
            createRegularKeyInfo("'", '"'),
        ],
        [
            shiftKey,
            createRegularKeyInfo("z", "Z"),
            createRegularKeyInfo("x", "X"),
            createRegularKeyInfo("c", "C"),
            createRegularKeyInfo("v", "V"),
            createRegularKeyInfo("b", "B"),
            createRegularKeyInfo("n", "N"),
            createRegularKeyInfo("m", "M"),
            createRegularKeyInfo(",", "<"),
            createRegularKeyInfo(".", ">"),
            createRegularKeyInfo("/", "?"),
            shiftKey,
        ],
        [spaceKey],
    ];
}
